import { getAuth, getIdToken } from "firebase/auth";
import axios from "axios";
import { logger } from "../logging";

const projectName = import.meta.env.VITE_APP_FIREBASE_PROJECT;
let apiBaseURL = `https://us-central1-${projectName}.cloudfunctions.net/api`;
if (import.meta.env.VITE_APP_USE_FUNCTIONS_EMULATOR === "true") {
  apiBaseURL = `http://localhost:5001/${projectName}/us-central1/api`;
}

// #region Type Definitions

export interface MoreRewardsConfig {
  encryptedClientSiteKey: string;
  locationMappings: { [locationId: string]: string }; // locationID -> MoreRewards location code
}

export interface MoreRewardsOffer {
  offerId: string;
  offerName: string;
  offerDescription: string;
  offerCode: string;
  approvalStatus: string;
  offerType: string;
}

export interface GetOffersResponse {
  success: boolean;
  offers: MoreRewardsOffer[];
}

// #endregion Type Definitions

// #region API Functions

/**
 * Get available offers from MoreRewards API for a specific location
 */
export async function getMoreRewardsOffers(
  siteKey: string,
  locationID: string,
): Promise<GetOffersResponse> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw Error("No Firebase user was found");

  const idToken = await getIdToken(user);

  const endpoint = "more-rewards/offers";
  const fullApiRoute = `${apiBaseURL}/${endpoint}?siteKey=${encodeURIComponent(siteKey)}&locationID=${locationID}`;

  try {
    const response = await axios.get(fullApiRoute, {
      headers: {
        authorization: `Bearer ${idToken}`,
      },
      timeout: 30000, // 30 second timeout as mentioned in docs
    });
    return response.data as GetOffersResponse;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      logger.error("Error fetching MoreRewards offers:", err.response?.data);
      logger.error("Status:", err.response?.status);
      logger.error("Headers:", err.response?.headers);

      // Return a structured error response
      return {
        success: false,
        offers: [],
      };
    }
    throw err;
  }
}
// #endregion API Functions
